import * as React from 'react';
import {FormHelperText, FormControl, FormLabel, Box, TextField as TextFieldEl} from '@mui/material';
import { useMemo, memo } from 'react';

interface TextProps {
    variant?: 'outlined' | 'filled' | 'standard';
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    label?: string;
    description?: string;
    required?: boolean;
    value: string;
    error?: string;
    minLength?: number;
    maxLength?: number;
    autoCapitalize?: string;
    enabled?: boolean;
    visible?: boolean;
    appValidated?: boolean;
}

const Text = memo((props: TextProps) => {
    const {
        variant = 'outlined',
        onChange,
        label,
        description,
        required = false,
        value = '',
        error,
        minLength,
        maxLength,
        autoCapitalize,
        enabled = true,
        visible = true,
        appValidated = false
    } = props;

    const [appHasBeenValidated, setAppHasBeenValidated] = React.useState(false);

    React.useEffect(() => {
        if (appValidated) {
            setAppHasBeenValidated(true);
        }
    }, [appValidated]);

    const showRequiredError = useMemo(() =>
            required && appHasBeenValidated && !value,
        [required, appHasBeenValidated, value]
    );

    const showError = useMemo(() =>
            error !== undefined && error !== "",
        [error, value]
    );

    const hasError = showError || showRequiredError;
    const errorMessage = showRequiredError ? 'This field is required' : error;

    // Memoize computed values
    const showDescription = useMemo(() =>
            description !== undefined && description !== "",
        [description]
    );

    // Memoize input props to prevent object recreation on every render
    const inputProps = useMemo(() => {
        const props: any = {};
        if (minLength !== undefined) props.minLength = minLength;
        if (maxLength !== undefined) props.maxLength = maxLength;
        if (autoCapitalize !== undefined) props.autoCapitalize = autoCapitalize;
        return props;
    }, [minLength, maxLength, autoCapitalize]);

    // Use useCallback to wrap the onChange if needed
    const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(event);
    }, [onChange]);

    // Early return for hidden components
    if (!visible) {
        return null;
    }

    return (
        <Box sx={{ display: 'block' }}>
            <FormControl className="form-control" fullWidth={true}>
                <FormLabel required={required} color="secondary" className="form-separate-label">
                    {label}
                </FormLabel>
                <TextFieldEl
                    error={hasError}
                    value={value}
                    onChange={handleChange}
                    slotProps={{ htmlInput: inputProps }}
                    type='text'
                    variant={variant}
                    className='form-control-field'
                    disabled={!enabled}
                    sx={{ ".Mui-disabled": { opacity: 0.6 } }}
                    fullWidth
                />
                {showDescription && (
                    <FormHelperText>
                        {description}
                    </FormHelperText>
                )}
                {hasError && (
                    <FormHelperText error={true}>
                        {errorMessage}
                    </FormHelperText>
                )}
            </FormControl>
        </Box>
    );
});

Text.displayName = 'Text';

export default Text;
